/**
 * A store-only ZIP writer, so a multi-file download arrives as one archive.
 *
 * The alternative is one save dialog per file, which for a job with a handful of
 * exports is not a download so much as a chore. Nothing here compresses: evidence
 * is small, JSON-ish and often already packed, and DEFLATE would cost a dependency
 * for a few percent. Every reader accepts a stored entry.
 */

/** CRC-32 (IEEE), which the ZIP central directory requires per entry. */
const TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let value = i
    for (let bit = 0; bit < 8; bit++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
    table[i] = value >>> 0
  }
  return table
})()

function crc32(bytes: Uint8Array<ArrayBuffer>): number {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) crc = TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

export interface ZipEntry { path: string; bytes: Uint8Array<ArrayBuffer> }

/** DOS date/time, the only timestamp the base ZIP format carries. */
function dosTime(at: Date): { time: number; date: number } {
  return {
    time: (at.getHours() << 11) | (at.getMinutes() << 5) | (at.getSeconds() >> 1),
    date: ((at.getFullYear() - 1980) << 9) | ((at.getMonth() + 1) << 5) | at.getDate(),
  }
}

export function zip(entries: ZipEntry[], at: Date = new Date()): Blob {
  const { time, date } = dosTime(at)
  const encoder = new TextEncoder()
  const parts: BlobPart[] = []
  const central: Uint8Array<ArrayBuffer>[] = []
  let offset = 0

  for (const entry of entries) {
    const name = encoder.encode(entry.path) as Uint8Array<ArrayBuffer>
    const sum = crc32(entry.bytes)
    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true)
    local.setUint16(4, 20, true)          // version needed
    local.setUint16(6, 0x0800, true)      // UTF-8 names
    local.setUint16(8, 0, true)           // stored
    local.setUint16(10, time, true)
    local.setUint16(12, date, true)
    local.setUint32(14, sum, true)
    local.setUint32(18, entry.bytes.length, true)
    local.setUint32(22, entry.bytes.length, true)
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true)          // no extra field
    parts.push(local.buffer, name, entry.bytes)

    const header = new Uint8Array(46 + name.length)
    const view = new DataView(header.buffer)
    view.setUint32(0, 0x02014b50, true)
    view.setUint16(4, 20, true)           // version made by
    view.setUint16(6, 20, true)
    view.setUint16(8, 0x0800, true)
    view.setUint16(10, 0, true)
    view.setUint16(12, time, true)
    view.setUint16(14, date, true)
    view.setUint32(16, sum, true)
    view.setUint32(20, entry.bytes.length, true)
    view.setUint32(24, entry.bytes.length, true)
    view.setUint16(28, name.length, true)
    view.setUint32(42, offset, true)      // where this entry's local header starts
    header.set(name, 46)
    central.push(header)
    offset += 30 + name.length + entry.bytes.length
  }

  const size = central.reduce((total, header) => total + header.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)
  end.setUint16(8, entries.length, true)
  end.setUint16(10, entries.length, true)
  end.setUint32(12, size, true)
  end.setUint32(16, offset, true)
  return new Blob([...parts, ...central, end.buffer], { type: 'application/zip' })
}

/** Hand the browser a blob under a given filename, then release the object URL. */
export function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  saveUrl(url, filename)
  URL.revokeObjectURL(url)
}

/**
 * Download a URL in place. Signed file links are served as attachments, so the page stays.
 * A new tab opened after an awaited request is a popup that browsers may silently block.
 */
export function saveUrl(url: string, filename = '') {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
}
